import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOptionalTenant } from './TenantContext';
import { allowsTenantQueryOverride } from './controlPlaneHosts';

export const requiresTenantQuery = (hostname: string): boolean => {
  return allowsTenantQueryOverride(hostname);
};

export const addTenantToSearch = (search: string, tenantId: string): string => {
  const params = new URLSearchParams(search);
  params.set('tenant', tenantId);
  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
};

/**
 * Localhost and the shared dev control plane cannot resolve a tenant from their
 * hostname. Keep the resolved tenant in every same-origin URL so refreshes,
 * bookmarks, redirects, and old navigate('/admin/...') calls retain context.
 * Custom production domains continue resolving exclusively by hostname.
 */
export const TenantQueryController: React.FC = () => {
  const tenant = useOptionalTenant();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!tenant) {
      return;
    }
    if (!requiresTenantQuery(window.location.hostname)) {
      return;
    }

    const currentTenant = new URLSearchParams(location.search).get('tenant');
    // A different explicit tenant is intentional. TenantBootstrap owns the
    // transition and remounts the application after validating it.
    if (currentTenant) {
      return;
    }

    navigate(
      {
        pathname: location.pathname,
        search: addTenantToSearch(location.search, tenant.tenantId),
        hash: location.hash,
      },
      { replace: true }
    );
  }, [location.hash, location.pathname, location.search, navigate, tenant]);

  return null;
};
